/* ============================================================
   CARINA — drawer.js
   Off-Canvas-Drawer (Mobile Navigation, Cart Drawer)
   ============================================================ */

'use strict';

class DrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer-panel]');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.openBtns = document.querySelectorAll(`[data-drawer-open="${this.id}"]`);
    this.closeBtns = this.querySelectorAll('[data-drawer-close]');

    this.openBtns.forEach((btn) => btn.addEventListener('click', () => this.open()));
    this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Section editor support
    this.addEventListener('shopify:section:select', () => this.open());
    this.addEventListener('shopify:section:deselect', () => this.close());
  }

  isOpen() {
    return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
  }

  open() {
    if (!this.drawer) return;
    this.activeElement = document.activeElement;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.removeAttribute('inert');
    if (this.overlay) this.overlay.removeAttribute('hidden');
    document.body.classList.add('drawer-open');
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    const firstFocusable = this.drawer.querySelector(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();

    this._trapFocusHandler = (e) => this._trapFocus(e);
    this.drawer.addEventListener('keydown', this._trapFocusHandler);

    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    if (this.overlay) this.overlay.setAttribute('hidden', '');
    document.body.classList.remove('drawer-open');
    document.body.style.overflow = '';

    if (this._trapFocusHandler) {
      this.drawer.removeEventListener('keydown', this._trapFocusHandler);
    }

    if (this.activeElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }

    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  _trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.drawer.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[aria-hidden="true"]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}

// Drawer CSS (injected once)
const drawerStyle = document.createElement('style');
drawerStyle.textContent = `
  .site-header__nav-drawer {
    position: fixed;
    inset: 0;
    z-index: 500;
    pointer-events: none;
  }
  .site-header__nav-drawer .drawer-overlay {
    position: fixed;
    inset: 0;
    background: rgba(43, 43, 38, 0.4);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  .site-header__nav-drawer:not([aria-hidden="true"]) .drawer-overlay,
  .site-header__nav-drawer [data-drawer-panel][aria-hidden="false"] ~ .drawer-overlay {
    opacity: 1;
    pointer-events: auto;
  }
  [data-drawer-panel] {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(360px, 90vw);
    background: var(--color-bg);
    border-left: 1px solid var(--color-border);
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    overflow-y: auto;
    pointer-events: auto;
    padding: var(--space-6) var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
  }
  [data-drawer-panel][aria-hidden="false"] {
    transform: translateX(0);
  }
  [data-drawer-overlay] { pointer-events: auto; }
  [data-drawer-overlay][hidden] { display: none; }
  body.drawer-open { overflow: hidden; }
  .drawer-close-btn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .drawer-nav__list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .drawer-nav__item a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) 0;
    border-bottom: 1px solid var(--color-border);
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-text);
    text-decoration: none;
  }
  .drawer-nav__item a:hover { color: var(--color-primary); }
  .drawer-nav__item--highlight a { color: var(--color-primary); font-weight: 600; }
`;
if (!document.getElementById('carina-drawer-style')) {
  drawerStyle.id = 'carina-drawer-style';
  document.head.appendChild(drawerStyle);
}
